import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { ParameterField } from '../ParameterField';
import GroupingParameterField from '../GroupingParameterField';
import BooleanField from '../BooleanField';
import SelectField from '../SelectField';
import StringField from '../StringField';

import { AGGREGATIONS, ATTRIBUTE_TYPES } from '../../ts/ConfigurationConstants';
import { IValidator } from '../../ts/ParameterFieldBase';

export const validateSummaryTemplate: IValidator = (value: ISummaryOptions) => {
    const result: string[] = [];
    const summaryFormat = value?.summaryFormat;
    const groupSummaryFormat = value?.groupSummaryFormat;

    if (summaryFormat != null && summaryFormat.indexOf('{0}') === -1) {
        result.push('Total Summary template should contain value placeholder {0}');
    }

    if (groupSummaryFormat != null && groupSummaryFormat.indexOf('{0}') === -1) {
        result.push('Group Summary template should contain value placeholder {0}');
    }

    return result.length > 0 ? result : null;
};

export interface ISummaryOptions {
    aggregateGroup?: string;
    showSum?: boolean;
    summaryType?: string;
    summaryFormat?: string;
    showGroupSum?: boolean;
    groupSummaryType?: string;
    groupSummaryFormat?: string;
    showGroupSumInFooter?: boolean;
    groupSummaryAlignInColumn?: boolean;
}

export default function SummaryOptions(props: {
    value: ISummaryOptions;
    attributeType: string;

    onChange?(value: ISummaryOptions): void;
}) {
    const getValueCopy = () => {
        return props.value != null ? { ...props.value } : {};
    };

    const processOnChange = (value: ISummaryOptions) => {
        if (props.onChange != null) {
            props.onChange(value);
        }
    };

    const handleOnShowSumChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value) {
            valueCopy.showSum = value;
        } else {
            delete valueCopy.showSum;
        }

        processOnChange(valueCopy);
    };

    const handleShowGroupSumChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value) {
            valueCopy.showGroupSum = value;
        } else {
            delete valueCopy.showGroupSum;
        }

        processOnChange(valueCopy);
    };

    const handleSummaryTypeChange = (value: string) => {
        const valueCopy = getValueCopy();

        valueCopy.summaryType = value;
        valueCopy.groupSummaryType = value;
        processOnChange(valueCopy);
    };

    const handleGroupSummaryTypeChange = (value: string) => {
        const valueCopy = getValueCopy();

        valueCopy.groupSummaryType = value;
        processOnChange(valueCopy);
    };

    const handleOnSummaryFormatChange = (value: string) => {
        const valueCopy = getValueCopy();

        if (value != null && value !== '') {
            valueCopy.summaryFormat = value;
        } else {
            delete valueCopy.summaryFormat;
        }

        processOnChange(valueCopy);
    };

    const handleOnGroupSummaryFormatChange = (value: string) => {
        const valueCopy = getValueCopy();

        if (value != null && value !== '') {
            valueCopy.groupSummaryFormat = value;
        } else {
            delete valueCopy.groupSummaryFormat;
        }

        processOnChange(valueCopy);
    };

    const handleShowGroupSumInFooterChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value) {
            valueCopy.showGroupSumInFooter = value;
        } else {
            delete valueCopy.showGroupSumInFooter;
        }

        processOnChange(valueCopy);
    };

    const handleGroupSumAlignInColumnChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        if (value) {
            valueCopy.groupSummaryAlignInColumn = value;
        } else {
            delete valueCopy.groupSummaryAlignInColumn;
        }

        processOnChange(valueCopy);
    };

    const { value, attributeType } = props;

    // Return actual of default summary type
    const getSummaryType = (value: ISummaryOptions) => {
        if (value && value.summaryType != null) {
            return value.summaryType;
        } else {
            if (attributeType === ATTRIBUTE_TYPES.INTEGER || attributeType === ATTRIBUTE_TYPES.REAL) {
                return AGGREGATIONS.SUM;
            } else {
                return AGGREGATIONS.COUNT;
            }
        }
    };

    // Return actual of default group summary type
    const getGroupSummaryType = (value: ISummaryOptions) => {
        if (value && value.groupSummaryType != null) {
            return value.groupSummaryType;
        } else {
            if (attributeType === ATTRIBUTE_TYPES.INTEGER || attributeType === ATTRIBUTE_TYPES.REAL) {
                return AGGREGATIONS.SUM;
            } else {
                return AGGREGATIONS.COUNT;
            }
        }
    };

    const getSummaryTypes = (value: ISummaryOptions) => {
        let values: string[];

        //COUNT must be the first option because it is default value.
        if (
            (value.aggregateGroup != null && value.aggregateGroup.indexOf(AGGREGATIONS.COUNT) !== -1) ||
            value.aggregateGroup === AGGREGATIONS.SUM ||
            attributeType === ATTRIBUTE_TYPES.INTEGER ||
            attributeType === ATTRIBUTE_TYPES.REAL
        ) {
            values = [
                AGGREGATIONS.COUNT,
                AGGREGATIONS.COUNT_DISTINCT,
                AGGREGATIONS.AVG,
                AGGREGATIONS.MEDIAN,
                AGGREGATIONS.MAX,
                AGGREGATIONS.MIN,
                AGGREGATIONS.SUM,
                AGGREGATIONS.UNION
            ];
        } else if (
            attributeType === ATTRIBUTE_TYPES.STRING ||
            attributeType === ATTRIBUTE_TYPES.BOOLEAN ||
            attributeType === ATTRIBUTE_TYPES.LOGICAL
        ) {
            values = [AGGREGATIONS.COUNT, AGGREGATIONS.COUNT_DISTINCT, AGGREGATIONS.UNION];
        } else {
            values = [
                AGGREGATIONS.COUNT,
                AGGREGATIONS.COUNT_DISTINCT,
                AGGREGATIONS.MAX,
                AGGREGATIONS.MIN,
                AGGREGATIONS.UNION
            ];
        }

        return {
            values: values,
            display: values
        };
    };

    return (
        <>
            <GroupingParameterField title={'Total Summary'}>
                <div className="row">
                    <div className="col">
                        <ParameterField noTitle={true} clearable={false}>
                            <BooleanField
                                title={'Show Summary'}
                                value={value.showSum}
                                allowNull={false}
                                onChange={handleOnShowSumChange}
                            />
                        </ParameterField>
                    </div>

                    <div className="col">
                        <ParameterField title={'Summary Type'} clearable={false}>
                            <SelectField
                                value={getSummaryType(value)}
                                options={getSummaryTypes(value)}
                                onChange={handleSummaryTypeChange}
                            />
                        </ParameterField>
                    </div>

                    <div className="col">
                        <ParameterField title={'Format'} clearable={false}>
                            <StringField
                                value={value.summaryFormat}
                                placeholder={'{0}'}
                                onChange={handleOnSummaryFormatChange}
                            />
                        </ParameterField>
                    </div>
                </div>
            </GroupingParameterField>

            <GroupingParameterField title={'Group Summary'}>
                <div className="row">
                    <div className="col">
                        <ParameterField noTitle={true} clearable={false}>
                            <BooleanField
                                title={'Show Summary'}
                                value={value.showGroupSum}
                                allowNull={false}
                                onChange={handleShowGroupSumChange}
                            />
                        </ParameterField>
                    </div>

                    <div className="col">
                        <ParameterField title={'Summary Type'} clearable={false}>
                            <SelectField
                                value={getGroupSummaryType(value)}
                                options={getSummaryTypes(value)}
                                onChange={handleGroupSummaryTypeChange}
                            />
                        </ParameterField>
                    </div>

                    <div className="col">
                        <ParameterField title={'Format'} clearable={false}>
                            <StringField
                                value={value.groupSummaryFormat}
                                placeholder={'{0}'}
                                onChange={handleOnGroupSummaryFormatChange}
                            />
                        </ParameterField>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <ParameterField noTitle={true} clearable={false}>
                            <BooleanField
                                title={'Show in Group Footer'}
                                value={value.showGroupSumInFooter}
                                allowNull={false}
                                onChange={handleShowGroupSumInFooterChange}
                            />
                        </ParameterField>
                    </div>

                    <div className="col">
                        <ParameterField noTitle={true} clearable={false}>
                            <BooleanField
                                title={'Align by Column'}
                                value={value.groupSummaryAlignInColumn}
                                allowNull={false}
                                onChange={handleGroupSumAlignInColumnChange}
                            />
                        </ParameterField>
                    </div>
                </div>
            </GroupingParameterField>
        </>
    );
}
